import { Chip } from '@nespresso-design-system/components';
import useTranslations from '@/widgets/hooks/useTranslations';
import type {
  Filtering,
  FiltersAndSort,
  Sorting
} from '@/widgets/services/FilteringAndSortingService';
import type { Translations } from '../../../ProductDetailsHero/config/index.ts';
import { trackFiltersRemoval } from '../../Tracking.ts';
import styles from './FilterSelections.module.scss';
import useMediaQuery from '@/widgets/hooks/useMediaQuery.tsx';
import FilterSelectionsClearAll from './FilterSelectionsClearAll.tsx';
import type { Filter, FilterOption } from '@/domain/FilteringAndSorting.ts';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import type { Options } from '../../config/index.ts';

export type FilterSelectionsProps = {
  filtersAndSort: FiltersAndSort;
};

function FiltersApplied({ label }: { label: string }) {
  return (
    <div class={styles.totalFilters}>
      <span>{label}</span>
    </div>
  );
}

const handleClearAllFilters = (filtering: Filtering, sorting: Sorting) => {
  trackFiltersRemoval(filtering.currentFilters.value, 'clear all ext');

  filtering.clearAllFilters();
  sorting.clearSort();
};

export default function FilterSelections({
  filtersAndSort
}: FilterSelectionsProps) {
  const { hideAppliedFilters } = useWidgetOptions<Options>();
  const { isMobile } = useMediaQuery();
  const { t } = useTranslations<Translations>();
  const filtersCount = filtersAndSort.filtering.currentFilters.value.length;
  const filtersAppliedString = t('productList.filters.filterApplied', {
    count: filtersCount
  });
  const { filtering, sorting } = filtersAndSort;

  if (hideAppliedFilters && !isMobile) {
    return null;
  }

  return (
    <section>
      {filtersCount && isMobile && (
        <div class={styles.totalFiltersAndClearAll}>
          <FiltersApplied label={filtersAppliedString} />
          <FilterSelectionsClearAll
            label={t('productList.filters.cleanFilters')}
            onClick={() => {
              handleClearAllFilters(filtering, sorting);
            }}
          />
        </div>
      )}
      {!isMobile && (
        <>
          <FiltersApplied label={filtersAppliedString} />
          <ul class={styles.filtersContainer}>
            {filtersAndSort.filtering.currentFilters.value.map(
              ([filter, filterOption]) => (
                <li key={`${filter.id}-${filterOption.id}`}>
                  <Chip
                    aria-label={filterOption.label}
                    label={filterOption.label}
                    onClick={() => {
                      filtersAndSort.filtering.updateFilter(
                        filter.id,
                        filterOption.id,
                        false
                      );
                      const selections: [Filter, FilterOption][] = [
                        [filter, filterOption]
                      ];
                      trackFiltersRemoval(selections, 'clear chip');
                    }}
                    intent="dismissible"
                  />
                </li>
              )
            )}
            {filtersCount > 0 && (
              <li>
                <FilterSelectionsClearAll
                  label={t('productList.filters.cleanFilters')}
                  onClick={() => {
                    handleClearAllFilters(filtering, sorting);
                  }}
                />
              </li>
            )}
          </ul>
        </>
      )}
    </section>
  );
}
